import { useEffect, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useToast } from '@/components/Toast';
import { toYmd } from '@/lib/localDate';
import type { Poll } from '@flock-types/polls.types';

interface Props {
  poll: Poll;
  extendPoll: (closesAt: string) => Promise<void>;
  isMutating: boolean;
  onClose: () => void;
}

/** Pick a new closing date for an active poll (Expo extend-picker port). */
export default function PollExtendModal({ poll, extendPoll, isMutating, onClose }: Props) {
  const { showToast } = useToast();
  const [date, setDate] = useState(() => {
    // Prefill: current closes_at if still in the future, else a week out
    const initial = poll.closes_at ? new Date(poll.closes_at) : new Date();
    if (initial < new Date()) {
      const weekOut = new Date();
      weekOut.setDate(weekOut.getDate() + 7);
      return toYmd(weekOut);
    }
    return toYmd(initial);
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleExtend = async () => {
    if (!date) return;
    try {
      // End of the chosen day, local time
      const [y, m, d] = date.split('-').map((n) => parseInt(n, 10));
      await extendPoll(new Date(y, m - 1, d, 23, 59, 59).toISOString());
      onClose();
    } catch {
      showToast('Failed to extend poll. Please try again.', 'error');
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-semibold text-flock-navy-900">Extend Poll</h2>
          <button onClick={onClose} className="p-1 text-flock-navy-400 hover:text-flock-navy-600">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <label className="mb-1 block text-xs font-medium text-flock-navy-600">
          New closing date
        </label>
        <input
          type="date"
          value={date}
          min={toYmd(new Date())}
          onChange={(e) => setDate(e.target.value)}
          className="w-full rounded-lg border border-flock-gray-200 bg-flock-navy-50 px-3 py-2 text-sm outline-none focus:border-flock-navy-400"
        />

        <div className="mt-4 flex gap-3">
          <button
            onClick={onClose}
            className="min-h-touch flex-1 rounded-lg border border-flock-gray-300 px-4 py-2.5 text-sm font-medium text-flock-navy-600 transition hover:bg-flock-navy-50"
          >
            Cancel
          </button>
          <button
            onClick={handleExtend}
            disabled={!date || isMutating}
            className="min-h-touch flex-1 rounded-lg bg-flock-navy-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-flock-navy-700 disabled:opacity-50"
          >
            {isMutating ? 'Extending…' : 'Extend'}
          </button>
        </div>
      </div>
    </div>
  );
}
